import { useState, useEffect } from 'react';
import { format, addDays, addMinutes, parse, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addMonths, subMonths, eachDayOfInterval, isSameDay, isSameMonth } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@shared/store/authStore';
import { notifyAdminAboutBooking } from '../utils/bookingNotifications';

interface CourtType {
  type: 'tennis' | 'padel';
  name: string;
  price: number;
  description: string;
  image: string;
  courts: number[]; // Court IDs for Universe API
}

interface TimeSlot {
  time: string;
  available: boolean;
}

interface Employee {
  Id: number;
  first_name: string;
  last_name: string;
  middle_name?: string;
  spec_name: string;
  services?: any[];
}

const courtTypes: CourtType[] = [
  {
    type: 'tennis',
    name: 'Теннисные корты',
    price: 1500,
    description: 'Профессиональные теннисные корты с искусственным покрытием',
    image: '🎾',
    courts: [1, 2, 3, 4] // Court IDs 1-4 for tennis
  },
  {
    type: 'padel',
    name: 'Падел корты',
    price: 1800,
    description: 'Падел корты со стеклянными стенами',
    image: '🏓',
    courts: [10, 11, 8] // Court IDs: Падел 1=10, Падел 2=11, Падел 3=8
  }
];

export function CourtsPage() {
  const navigate = useNavigate();
  const { user, apiClient } = useAuthStore();

  // Temporary mock user for development
  const mockUser = user || {
    id: 1,
    firstName: 'Гость',
    lastName: 'Пользователь',
    phone: '+7 000 000 00 00',
    isAdmin: false,
    createdAt: new Date().toISOString()
  };

  const [selectedCourtType, setSelectedCourtType] = useState<CourtType | null>(courtTypes[0]);
  const [selectedDate, setSelectedDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [selectedTimeSlots, setSelectedTimeSlots] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);

  // Personal trainer states
  const [personalTrainerEnabled, setPersonalTrainerEnabled] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState<Employee | null>(null);
  const [availableTrainers, setAvailableTrainers] = useState<Employee[]>([]);
  const [loadingTrainers, setLoadingTrainers] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // API client is now initialized automatically in authStore

  // Get time slots from Universe API only
  const generateAvailableTimeSlots = async (courtType: CourtType, date: string): Promise<TimeSlot[]> => {
    if (!apiClient) {
      return [];
    }

    try {
      // Get free time for all courts of this type at once
      const courtFreeTimeResults = await apiClient.getCourtFreeTime(courtType.courts, date);
      
      
      // Combine all free times and remove duplicates
      const combinedTimes = new Set<string>();

      // courtFreeTimeResults is array of court objects from API response
      if (Array.isArray(courtFreeTimeResults) && courtFreeTimeResults.length > 0) {
        courtFreeTimeResults.forEach(courtData => {
          if (courtData && Array.isArray(courtData.free_time)) {
            // Process each free time block
            courtData.free_time.forEach(freeBlock => {
              if (freeBlock && Array.isArray(freeBlock.interval_court)) {
                // Process each interval in the block
                freeBlock.interval_court.forEach(interval => {
                  // Добавляем слоты только если есть услуги (total_price > 0)
                  if (interval && interval.time_from && interval.total_price > 0 && Array.isArray(interval.services_court) && interval.services_court.length > 0) {
                    combinedTimes.add(interval.time_from);
                  }
                });
              }
            });
          }
        });
      }
      
      
      // Convert to TimeSlot array and sort
      const slots: TimeSlot[] = Array.from(combinedTimes)
        .map(time => ({ time, available: true }))
        .sort((a, b) => a.time.localeCompare(b.time));
      
      return slots;
    } catch (error) {
      return [];
    }
  };

  // Load available tennis trainers
  const loadAvailableTrainers = async () => {
    if (!apiClient || !selectedDate || selectedTimeSlots.length === 0) {
      setAvailableTrainers([]);
      return;
    }

    setLoadingTrainers(true);
    try {
      // Get all employees
      const allEmployees = await apiClient.getEmployees();

      // Filter only tennis trainers
      const tennisTrainers = allEmployees.filter((emp: Employee) => {
        const specName = emp.spec_name?.toLowerCase() || '';
        return specName.includes('теннис') || specName.includes('tennis');
      });

      // Check which trainers are available for the selected time slots
      const availableTrainersForSlots: Employee[] = [];

      for (const trainer of tennisTrainers) {
        // Get trainer's free time for selected date
        try {
          const freeTimeData = await apiClient.getEmpFreeTime(trainer.Id, selectedDate);

          if (Array.isArray(freeTimeData) && freeTimeData.length > 0) {
            const empData = freeTimeData[0];

            // Check if trainer is free for ALL selected time slots
            let isAvailableForAllSlots = true;

            for (const selectedTime of selectedTimeSlots) {
              let slotIsFree = false;

              if (empData.free_time && Array.isArray(empData.free_time)) {
                for (const timeRange of empData.free_time) {
                  const [rangeStartHour, rangeStartMinute] = timeRange.time_from.split(':').map(Number);
                  const [rangeEndHour, rangeEndMinute] = timeRange.time_to.split(':').map(Number);
                  const [slotHour, slotMinute] = selectedTime.split(':').map(Number);

                  const rangeStartMinutes = rangeStartHour * 60 + rangeStartMinute;
                  const rangeEndMinutes = rangeEndHour * 60 + rangeEndMinute;
                  const slotMinutes = slotHour * 60 + slotMinute;
                  const slotEndMinutes = slotMinutes + 30; // 30-minute slots

                  if (slotMinutes >= rangeStartMinutes && slotEndMinutes <= rangeEndMinutes) {
                    slotIsFree = true;
                    break;
                  }
                }
              }

              if (!slotIsFree) {
                isAvailableForAllSlots = false;
                break;
              }
            }

            if (isAvailableForAllSlots) {
              availableTrainersForSlots.push(trainer);
            }
          }
        } catch (error) {
          console.error('Error checking trainer availability:', error);
        }
      }

      setAvailableTrainers(availableTrainersForSlots);
    } catch (error) {
      console.error('Error loading trainers:', error);
      setAvailableTrainers([]);
    } finally {
      setLoadingTrainers(false);
    }
  };

  // Load trainers when personal trainer is enabled and slots are selected
  useEffect(() => {
    if (personalTrainerEnabled && selectedTimeSlots.length > 0) {
      loadAvailableTrainers();
    } else {
      setAvailableTrainers([]);
      setSelectedTrainer(null);
    }
  }, [personalTrainerEnabled, selectedTimeSlots, selectedDate, apiClient]);

  // Load time slots when court type or date changes
  useEffect(() => {
    const loadTimeSlots = async () => {
      if (!selectedCourtType || !selectedDate || !apiClient) {
        setTimeSlots([]);
        return;
      }

      setLoadingTimeSlots(true);
      try {
        const slots = await generateAvailableTimeSlots(selectedCourtType, selectedDate);
        setTimeSlots(slots);
      } catch (error) {
        setTimeSlots([]);
      } finally {
        setLoadingTimeSlots(false);
      }
    };

    loadTimeSlots();
  }, [selectedCourtType, selectedDate, apiClient]);

  // Generate available dates (next 14 days) for mobile
  const availableDates = Array.from({ length: 14 }, (_, i) => {
    const date = addDays(new Date(), i);
    return {
      value: format(date, 'yyyy-MM-dd'),
      label: format(date, 'dd MMM', { locale: ru }),
      isToday: i === 0,
    };
  });

  // Generate calendar days for desktop
  const generateCalendarDays = () => {
    const start = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 1 });
    
    return eachDayOfInterval({ start, end });
  };

  const calendarDays = generateCalendarDays();
  
  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));

  const handleBooking = async () => {
    if (!selectedCourtType || !selectedDate || selectedTimeSlots.length === 0 || !mockUser || !apiClient) return;

    // Validate trainer selection if personal trainer is enabled
    if (personalTrainerEnabled && !selectedTrainer) {
      alert('Пожалуйста, выберите персонального тренера');
      return;
    }

    try {
      setIsLoading(true);

      // Get fresh free time data for all courts to find available court for each slot
      const courtFreeTimeResults = await apiClient.getCourtFreeTime(selectedCourtType.courts, selectedDate);

      for (const timeSlot of selectedTimeSlots) {
        // Find a free court for this specific time slot
        let availableCourtId: number | null = null;

        for (const courtData of courtFreeTimeResults) {
          if (courtData && Array.isArray(courtData.free_time)) {
            for (const freeBlock of courtData.free_time) {
              if (freeBlock && Array.isArray(freeBlock.interval_court)) {
                for (const interval of freeBlock.interval_court) {
                  if (interval && interval.time_from === timeSlot && interval.total_price > 0) {
                    availableCourtId = courtData.court_id;
                    break;
                  }
                }
              }
              if (availableCourtId) break;
            }
          }
          if (availableCourtId) break;
        }

        if (!availableCourtId) {
          alert(`Корт недоступен для времени ${timeSlot}. Пожалуйста, обновите страницу и выберите другое время.`);
          setIsLoading(false);
          return;
        }

        // Calculate time_to (30 minutes later for 30-minute interval)
        const [hours, minutes] = timeSlot.split(':').map(Number);

        // Format time in local timezone (Moscow time) without UTC conversion
        const endHour = Math.floor((hours * 60 + minutes + 30) / 60);
        const endMinute = (hours * 60 + minutes + 30) % 60;

        const timeFromStr = `${selectedDate} ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
        const timeToStr = `${selectedDate} ${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}:00`;

        const comment = personalTrainerEnabled && selectedTrainer
          ? `${selectedCourtType.name} + Тренер: ${selectedTrainer.first_name} ${selectedTrainer.last_name}`
          : selectedCourtType.name;

        console.log(`🎾 Бронирование корта: court_id=${availableCourtId}, time=${timeSlot}, trainer=${selectedTrainer?.first_name || 'нет'}`);

        // Book court with or without trainer (single request)
        const bookingData: any = {
          court_id: availableCourtId,
          clientid: mockUser.id,
          time_from: timeFromStr,
          time_to: timeToStr,
          comment
        };

        // Add trainer to the same booking if selected
        if (personalTrainerEnabled && selectedTrainer) {
          bookingData.empid = selectedTrainer.Id;
        }

        await apiClient.createCourtBooking(bookingData);
      }

      // Calculate continuous time range from first to last slot
      const sortedSlots = [...selectedTimeSlots].sort();
      const firstSlot = sortedSlots[0];
      const lastSlot = sortedSlots[sortedSlots.length - 1];

      // Calculate end time by adding 30 minutes to last slot
      const [lastHour, lastMinute] = lastSlot.split(':').map(Number);
      const endMinute = lastMinute + 30;
      const endHour = endMinute >= 60 ? lastHour + 1 : lastHour;
      const endTime = `${endHour.toString().padStart(2, '0')}:${(endMinute % 60).toString().padStart(2, '0')}`;

      const timeRange = `${firstSlot}-${endTime}`;
      const trainerInfo = personalTrainerEnabled && selectedTrainer
        ? `\\nТренер: ${selectedTrainer.first_name} ${selectedTrainer.last_name}`
        : '';

      // Отправляем уведомление в Telegram бот админа
      await notifyAdminAboutBooking({
        clientId: mockUser.id,
        clientName: mockUser.firstName || '',
        date: selectedDate,
        time: timeRange,
        serviceName: selectedCourtType.name,
        employeeName: selectedTrainer ? `${selectedTrainer.first_name} ${selectedTrainer.last_name}` : undefined
      });

      alert(`Корты забронированы!\\n\\nТип: ${selectedCourtType.name}\\nДата: ${selectedDate}\\nВремя: ${timeRange}${trainerInfo}`);
      navigate('/profile');
    } catch (error) {
      alert('Ошибка при бронировании. Попробуйте снова.');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleTimeSlot = (time: string) => {
    setSelectedTimeSlots(prev => {
      if (prev.includes(time)) {
        return prev.filter(t => t !== time);
      } else {
        return [...prev, time].sort();
      }
    });
  };

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center mb-4 hover:opacity-75"
            style={{ color: 'var(--text-secondary)' }}
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Назад
          </button>
          
          <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Бронирование кортов</h1>
        </div>

        {/* Court Type Selection */}
        <div className="rounded-2xl p-6 shadow-sm mb-6" style={{ backgroundColor: 'var(--card-bg)' }}>
          <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Тип корта</h2>
          <div className="flex space-x-4">
            {courtTypes.map((courtType) => (
              <button
                key={courtType.type}
                onClick={() => {
                  setSelectedCourtType(courtType);
                  setSelectedTimeSlots([]); // Clear selected times when changing court type
                }}
                className={`flex-1 p-4 border rounded-lg text-center transition-colors ${
                  selectedCourtType?.type === courtType.type 
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-2xl mb-2">{courtType.image}</div>
                <div className="font-medium" style={{ color: 'var(--text-primary)' }}>{courtType.name}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Desktop Calendar */}
        {!isMobile && (
          <div className="rounded-2xl p-6 shadow-sm mb-6" style={{ backgroundColor: 'var(--card-bg)' }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Выберите дату</h2>
              <div className="flex items-center space-x-4">
                <button
                  onClick={prevMonth}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <h3 className="text-xl font-semibold min-w-[200px] text-center" style={{ color: 'var(--text-primary)' }}>
                  {format(currentMonth, 'LLLL yyyy', { locale: ru })}
                </h3>
                <button
                  onClick={nextMonth}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {/* Day headers */}
              {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((day) => (
                <div key={day} className="p-3 text-center text-sm font-medium text-gray-500">
                  {day}
                </div>
              ))}
              
              {/* Calendar days */}
              {calendarDays.map((day) => {
                const dayString = format(day, 'yyyy-MM-dd');
                const isSelected = selectedDate === dayString;
                const isToday = isSameDay(day, new Date());
                const isCurrentMonth = isSameMonth(day, currentMonth);
                const isPast = day < new Date() && !isToday;
                
                return (
                  <button
                    key={dayString}
                    onClick={() => {
                      if (!isPast) {
                        setSelectedDate(dayString);
                        setSelectedTimeSlots([]);
                      }
                    }}
                    disabled={isPast}
                    className={`p-3 text-center rounded-lg transition-colors ${
                      isSelected
                        ? 'bg-emerald-500 text-white'
                        : isToday
                        ? 'bg-emerald-100 text-emerald-700 font-medium'
                        : isCurrentMonth
                        ? isPast
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-gray-900 hover:bg-gray-100'
                        : 'text-gray-400'
                    }`}
                  >
                    {format(day, 'd')}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Mobile Date Selection */}
        {isMobile && (
          <div className="rounded-2xl p-6 shadow-sm mb-6" style={{ backgroundColor: 'var(--card-bg)' }}>
            <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Выберите дату</h2>
            <div className="flex space-x-2 overflow-x-auto pb-2">
              {availableDates.map((date) => (
                <button
                  key={date.value}
                  onClick={() => {
                    setSelectedDate(date.value);
                    setSelectedTimeSlots([]);
                  }}
                  className={`flex-shrink-0 p-3 border rounded-lg text-center transition-colors min-w-[80px] ${
                    selectedDate === date.value
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 hover:border-emerald-500 hover:bg-emerald-50'
                  }`}
                >
                  <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{date.label}</div>
                  {date.isToday && <div className="text-xs text-emerald-600">Сегодня</div>}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Time Selection */}
        {selectedDate && (
          <div className="rounded-2xl p-6 shadow-sm mb-6" style={{ backgroundColor: 'var(--card-bg)' }}>
            <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
              Выберите время
              {selectedTimeSlots.length > 0 && (
                <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                  {selectedTimeSlots.length} выбрано
                </span>
              )}
            </h2>

            {loadingTimeSlots ? (
              <div className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
                Загрузка свободного времени...
              </div>
            ) : timeSlots.length > 0 ? (
              <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                {timeSlots.map((slot) => {
                  const isSelected = selectedTimeSlots.includes(slot.time);
                  return (
                    <button
                      key={slot.time}
                      onClick={() => toggleTimeSlot(slot.time)}
                      className={`p-3 border-2 rounded-xl text-center transition-all duration-200 transform ${
                        isSelected
                          ? 'border-emerald-500 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-lg scale-105 ring-2 ring-emerald-200'
                          : 'border-gray-200 hover:border-emerald-400 hover:bg-emerald-50 hover:shadow-md hover:scale-102 text-gray-900'
                      }`}
                    >
                      <div className={`font-semibold text-sm ${isSelected ? 'text-white' : ''}`}
                           style={{ color: isSelected ? 'white' : 'var(--text-primary)' }}>
                        {slot.time}
                      </div>
                      {isSelected && (
                        <div className="text-xs text-emerald-100 mt-1">
                          ✓ Выбрано
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
                На выбранную дату нет свободного времени
              </div>
            )}
          </div>
        )}

        {/* Booking Summary */}
        {selectedTimeSlots.length > 0 && (
          <div className="rounded-2xl p-6 shadow-lg border-2" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Ваша бронь</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Корт:</span>
                  <span className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{selectedCourtType?.name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Дата:</span>
                  <span className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{format(new Date(selectedDate), 'dd MMMM yyyy', { locale: ru })}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Время:</span>
                  <div className="text-right">
                    {(() => {
                      // Группируем последовательные слоты в диапазоны
                      const sorted = [...selectedTimeSlots].sort();
                      const ranges: string[] = [];
                      let rangeStart = sorted[0];
                      let prevSlot = sorted[0];

                      for (let i = 1; i <= sorted.length; i++) {
                        const curr = sorted[i];
                        const prevDate = parse(prevSlot, 'HH:mm', new Date());
                        const prevEnd = format(addMinutes(prevDate, 30), 'HH:mm');

                        if (i < sorted.length && curr === prevEnd) {
                          // Слоты последовательные — продолжаем диапазон
                          prevSlot = curr;
                        } else {
                          // Разрыв или конец — закрываем диапазон
                          const endDate = parse(prevSlot, 'HH:mm', new Date());
                          const endTime = format(addMinutes(endDate, 30), 'HH:mm');
                          ranges.push(`${rangeStart}-${endTime}`);
                          if (i < sorted.length) {
                            rangeStart = curr;
                            prevSlot = curr;
                          }
                        }
                      }

                      return (
                        <span className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                          {ranges.join(', ')}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Selected Time Slots Preview */}
              <div className="mt-4">
                <span className="text-xs font-medium mb-2 block" style={{ color: 'var(--text-secondary)' }}>Выбранные слоты:</span>
                <div className="flex flex-wrap gap-1">
                  {selectedTimeSlots.map((time, index) => (
                    <span
                      key={time}
                      className="inline-flex items-center px-2 py-1 rounded-md bg-emerald-500 text-white text-xs font-medium"
                    >
                      {time}
                      {index < selectedTimeSlots.length - 1 && (
                        <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Personal Trainer Toggle */}
            <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
              <label className="flex items-center cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={personalTrainerEnabled}
                    onChange={(e) => setPersonalTrainerEnabled(e.target.checked)}
                    className="w-5 h-5 rounded cursor-pointer"
                    style={{
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      backgroundColor: personalTrainerEnabled ? '#10b981' : 'transparent',
                      borderWidth: '2px',
                      borderColor: personalTrainerEnabled ? '#10b981' : 'var(--border-color)',
                      outline: 'none'
                    }}
                  />
                  {personalTrainerEnabled && (
                    <svg
                      className="absolute top-0 left-0 w-5 h-5 pointer-events-none"
                      style={{ color: 'white' }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <span className="ml-3 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  🎾 Персональный тренер по теннису
                </span>
              </label>
            </div>

            {/* Trainer Selection */}
            {personalTrainerEnabled && (
              <div className="mt-4">
                {loadingTrainers ? (
                  <div className="text-center py-4" style={{ color: 'var(--text-secondary)' }}>
                    Загрузка доступных тренеров...
                  </div>
                ) : availableTrainers.length > 0 ? (
                  <div>
                    <h4 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Выберите тренера:</h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {availableTrainers.map((trainer) => (
                        <button
                          key={trainer.Id}
                          onClick={() => setSelectedTrainer(trainer)}
                          className="w-full p-3 border-2 rounded-lg text-left transition-all hover:border-emerald-400"
                          style={{
                            backgroundColor: selectedTrainer?.Id === trainer.Id ? 'rgba(16, 185, 129, 0.25)' : 'var(--card-bg)',
                            borderColor: selectedTrainer?.Id === trainer.Id ? '#10b981' : 'rgba(156, 163, 175, 0.4)'
                          }}
                        >
                          <div className="flex items-center space-x-3">
                            {/* Radio button indicator */}
                            <div className="relative flex-shrink-0">
                              <div
                                className="w-6 h-6 rounded-full border-2 flex items-center justify-center"
                                style={{
                                  borderColor: selectedTrainer?.Id === trainer.Id ? '#10b981' : 'rgba(156, 163, 175, 0.6)',
                                  backgroundColor: selectedTrainer?.Id === trainer.Id ? '#10b981' : 'transparent'
                                }}
                              >
                                {selectedTrainer?.Id === trainer.Id && (
                                  <div className="w-3 h-3 rounded-full bg-white"></div>
                                )}
                              </div>
                            </div>

                            <div className="flex-1">
                              <div className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                                {trainer.first_name} {trainer.last_name}
                              </div>
                              <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>{trainer.spec_name}</div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    К сожалению, нет доступных тренеров на выбранные слоты
                  </div>
                )}
              </div>
            )}

            <button
              onClick={handleBooking}
              disabled={isLoading}
              className="w-full px-8 py-4 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-xl hover:from-emerald-700 hover:to-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-lg shadow-lg transition-all duration-200"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Бронирую...
                </div>
              ) : (
                `Забронировать ${selectedTimeSlots.length} слот${selectedTimeSlots.length > 1 ? (selectedTimeSlots.length > 4 ? 'ов' : 'а') : ''}`
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}